import { AxiosError } from 'axios';
import { QueryClient, keepPreviousData } from '@tanstack/react-query';

/**
 * Configuración de TanStack Query.
 *
 * No se reintenta ante 4xx: un 403 no mejora repitiéndolo, y reintentar un 401
 * pelearía con el refresco automático del cliente HTTP.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status !== undefined && status >= 400 && status < 500) return false;
  }
  return failureCount < 2;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,

      /**
       * Al cambiar un filtro, la pantalla NO se vacía.
       *
       * ── El defecto que corrige ─────────────────────────────────────────────
       * Los filtros forman parte de la clave de caché, así que cambiar uno crea una
       * consulta nueva sin datos: `isLoading` pasaba a `true`, `DataBoundary`
       * mostraba el esqueleto, y la tabla y los gráficos desaparecían para volver
       * medio segundo después. En una aplicación de una sola página eso se ve y se
       * siente exactamente como una recarga del servidor, aunque no lo sea.
       *
       * Con `keepPreviousData` los datos anteriores permanecen visibles mientras
       * llegan los nuevos. La transición pasa a ser una sustitución en su sitio, y
       * `isFetching` —que las tablas ya usan— basta para indicar que se está
       * actualizando.
       *
       * Va en la configuración GLOBAL y no en cada llamada: hay veinte consultas y
       * ninguna quiere el parpadeo. Ponerlo caso por caso garantizaba que la
       * siguiente que alguien escribiera lo tuviera otra vez.
       */
      placeholderData: keepPreviousData,
    },
    mutations: { retry: false },
  },
});

/** Claves de caché centralizadas: evita invalidaciones que no coinciden. */
export const queryKeys = {
  me: ['auth', 'me'] as const,
  filterOptions: ['catalog', 'filter-options'] as const,
  syncStatus: ['sync', 'status'] as const,
  syncRuns: (params: unknown) => ['sync', 'runs', params] as const,
  syncErrors: (runId: string, params: unknown) => ['sync', 'errors', runId, params] as const,
  dashboard: (filters: unknown) => ['dashboard', 'summary', filters] as const,
  moduleSummary: (moduleKey: string, filters: unknown) =>
    ['module', moduleKey, 'summary', filters] as const,
  moduleRows: (moduleKey: string, params: unknown) =>
    ['module', moduleKey, 'rows', params] as const,
  laborRelations: (params: unknown) => ['labor-relations', params] as const,
  inspections: (params: unknown) => ['inspections', params] as const,
  findings: (params: unknown) => ['findings', params] as const,
  users: (params: unknown) => ['users', params] as const,
  roles: ['roles'] as const,
  audit: (params: unknown) => ['audit', params] as const,
  settings: ['settings', 'parameters'] as const,
};
